import React, { useEffect, useState, useMemo } from 'react'
import AttachImageToTree from '@fs/zion-attach-image-to-tree'
import { InfoSheet } from '@fs/zion-ui'
import { useQueryParams } from '@fs/zion-router'
import { getSessionType, sessionTypes } from '@fs/zion-session'
import zionDebug from '@fs/zion-debug'
import { getImage, getNestedOrFloatingElementById, useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import { useOverlayContext } from '../../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2DispatchContext, useR2StateContext } from '../../providers/r2Provider/R2Context'
import useRecords from '../../views/indexing/hooks/useRecords'
import { useSharedBrowserStateContext } from '../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import useInfoSheetOrientationCachedState from '../../../shared/hooks/useInfoSheetOrientationCachedState'
import RipInfoSheetBody from './SideSheetBody'
import useAttachImageToTreeData from '../../hooks/useAttachImageToTreeData'
import useNamesExperienceOptIn from '../../hooks/useNamesExperienceOptIn'
import useRecordOutlineHighlightsEnabled from '../../hooks/useRecordOutlineHighlightsEnabled'

const debug = zionDebug('siv:RipInfoSheetWrapper')

const SideSheet = ({ loading, imageTitle }) => {
  const { infoSheetOverlay, openOverlay, allPanelsClosed, attachToTreeOverlay, topMostPanel, inPanelStack } =
    useOverlayContext()
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const sharedBrowserState = useSharedBrowserStateContext()
  const currentImage = getImage(zionSlsState.images, zionSlsState.currentImageArk)
  const h = useSlsHighlightsContext()
  const { isOptedIn, loading: optInLoading } = useNamesExperienceOptIn()
  const recordOutlineEnabled = useRecordOutlineHighlightsEnabled()

  const thumbnails = sharedBrowserState.thumbnails
  const { imageHasData } = useRecords({
    images: zionSlsState?.images,
    currentImageArk: zionSlsState?.currentImageArk,
    thumbnails,
  })

  const { isBottomSheet, setInfoSheetOrientation } = useInfoSheetOrientationCachedState()
  const attachImageToTreeData = useAttachImageToTreeData(imageTitle)

  const { query, removeQuery } = useQueryParams({ replaceState: true })
  const personId = query?.personArk?.split('/').find((str) => str.includes('1:1'))
  const action = query?.action

  // Get the current view title based on query and mode
  const currentViewTitle = useMemo(() => {
    switch (query.view) {
      case 'index':
        return t('headerNavigation.names', 'Names')
      case 'fullText':
        return t('headerNavigation.transcript', 'Transcript')
      default:
        return t('headerNavigation.information', 'Information')
    }
  }, [query.view, t])

  const [once, setOnce] = useState(false)
  const [attachRestored, setAttachRestored] = useState(false)

  // FSS-14845: reopen the attach flow after the sign-in round-trip. It fetches its own tree data, so
  // gate on the image (not `loading`) to avoid waiting behind the names skeleton. Only a user session
  // can attach, so drop the param instead of reopening the stuck flow for anyone else.
  useEffect(() => {
    if (action === 'attach' && !attachRestored && sharedBrowserState.imageLoaded) {
      if (getSessionType() === sessionTypes.userSession) {
        openOverlay('mainInfosheet')
        openOverlay('attachToTreeOverlay')
      }
      removeQuery('action')
      setAttachRestored(true)
    }
  }, [action, attachRestored, sharedBrowserState.imageLoaded, openOverlay, removeQuery])

  // Every record on the image keeps its outline. Record outlines are always on: unlike the name
  // highlights, they do not come and go with a hover in the Names list, and they stay while a person
  // is open too (PersonOverviewV2 passes every rect as well).
  // Staged here rather than written directly to the record highlight API, since zion-sls's own
  // internal sync effect overwrites that API whenever its state changes - see useDisplayHighlights.js.
  // Use the initialized state rather than re-reading the image: useImageHighlightInitialization
  // already merged in the neighbors' rects and filtered them to this image, which is the only way a
  // record gets an outline on a page where it is a dangling reference with no rects of its own.
  // Undefined (not []) with the flag off, so zion-sls keeps deriving the record highlights itself.
  const recordHighlightsForDisplay = useMemo(() => {
    if (!recordOutlineEnabled) return undefined
    return r2State.recordHighlights ?? []
  }, [recordOutlineEnabled, r2State.recordHighlights])

  // The split screen needs the reference image's own rects. zion-sls filters what it is given by
  // imageId, so handing it the main image's rects would drop them all and outline nothing.
  const referenceRecordHighlightsForDisplay = useMemo(() => {
    if (!recordOutlineEnabled) return undefined
    return r2State.referenceRecordHighlights ?? []
  }, [recordOutlineEnabled, r2State.referenceRecordHighlights])

  // Where should this global check go?  We need to reset highlights if all panels get closed
  useEffect(() => {
    if (allPanelsClosed() && query.view === 'index') {
      const fieldTypes = ['NAME_SURN', 'NAME_SUF', 'NAME_GN']
      const customHighlightsFilter = (hl) => hl.personIds.length > 0
      const splitScreenImageArk = thumbnails?.[r2State.referenceFocusedIndex]?.ark

      if (zionSlsState?.currentImageArk) {
        h.setDisplayHighlights({
          mainPartitionKey: zionSlsState.currentImageArk,
          fieldTypes,
          customFilter: customHighlightsFilter,
          recordHighlights: recordHighlightsForDisplay,
        })
      }

      if (r2State.splitScreen && !r2State.splitScreenImageGrid && splitScreenImageArk) {
        h.setDisplayHighlights({
          splitScreenPartitionKey: splitScreenImageArk,
          fieldTypes,
          customFilter: customHighlightsFilter,
          recordHighlights: referenceRecordHighlightsForDisplay,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allPanelsClosed,
    r2Dispatch,
    r2State.editHighlights,
    r2State.highlights,
    r2State.referenceEditHighlights,
    r2State.referenceFocusedIndex,
    r2State.splitScreen,
    zionSlsState.currentImageArk,
    thumbnails,
    query.view,
    r2State.splitScreenImageGrid,
    recordHighlightsForDisplay,
    referenceRecordHighlightsForDisplay,
  ])

  useEffect(() => {
    let mounted = true

    // Gameplan:
    // 1. The R2Image component sets the `imageLoaded` state to true once the image has fully loaded.
    // 2. After the image is loaded, the main InfoSheet overlay is opened.
    // 3. If a `personId` is present in the query parameters, the viewIndex (PersonOverview) overlay is opened on top of the main InfoSheet.
    // 4. This ensures the overlay stack is correctly ordered and the PersonOverview component can apply highlights properly.
    // Defer while action === 'attach': its own effect above consumes the param, then this runs a render
    // later with action cleared so the personArk cleanup below isn't skipped.
    if (!loading && !once && mounted && sharedBrowserState.imageLoaded && action !== 'attach') {
      if (action) {
        const selectedPerson = getNestedOrFloatingElementById([currentImage], personId)
        if (action === 'view' && selectedPerson && query.view !== 'fullText') {
          // The patron may have opened the person (and an edit panel over it) before loading finished.
          // Re-opening would pull viewIndex back on top of that panel, so leave the stack alone.
          if (!inPanelStack('viewIndex')) {
            openOverlay('viewIndex')
            r2Dispatch({ type: 'NAV_SELECT_TO_VIEW', value: { personId, recordId: selectedPerson.recordId } })
          }
        } else {
          // Person isn't found on this image, so remove the param
          removeQuery('action')
          removeQuery('personArk')
        }
      } else if (!topMostPanel()) {
        // If coming back from history viewer, this could be set
        r2Dispatch({ type: 'SET_SELECTED_PERSON_ID', value: null })
        removeQuery('personArk')
      }
      setOnce(true)
    }

    return () => {
      mounted = false
    }
  }, [
    action,
    loading,
    once,
    openOverlay,
    personId,
    r2Dispatch,
    zionSlsState.images,
    currentImage,
    removeQuery,
    topMostPanel,
    inPanelStack,
    sharedBrowserState.imageLoaded,
    query.view,
  ])

  debug({ imageHasData, zionSlsState })

  // Defer the whole sheet until the opt-in is definitive: size (md/lg) and dockable both derive from
  // isOptedIn, so rendering before it settles opens the sheet at the wrong size and then snaps it.
  // Effects above still run every render; only the sheet markup waits. The open state lives on
  // infoSheetOverlay (owned by the provider above this component), so the sheet mounts already-open
  // at the settled size rather than replaying the open transition.
  if (optInLoading) return null

  return (
    <InfoSheet
      dockable={!isOptedIn}
      size={isOptedIn ? 'lg' : 'md'}
      title={currentViewTitle}
      {...infoSheetOverlay}
      isBottomSheet={isBottomSheet}
      setIsBottomSheet={setInfoSheetOrientation}
    >
      <RipInfoSheetBody
        {...{
          imageHasData,
          isBottomSheet,
          loading,
        }}
      />
      {/* Rendered in every state now - FSS-14672 removed the indexed-names panel that used to render its own instance */}
      <AttachImageToTree {...attachImageToTreeData} attachToTreeFirstPanel={attachToTreeOverlay} />
    </InfoSheet>
  )
}

export default SideSheet
